import { getUser } from '../../../services/UserService';

const STORAGE_PREFIX = 'workflow-node-test-state';

const safeString = (value) => (value ?? '').toString().trim();

export const buildNodeTestStorageKey = ({
  workflowDefinitionID,
  nodeId,
  kind,
  subtype,
  panelKey,
} = {}) => {
  const parts = [
    STORAGE_PREFIX,
    safeString(workflowDefinitionID) || 'workflow-unknown',
    safeString(nodeId) || `node-${safeString(kind) || 'unknown'}-${safeString(subtype) || 'unknown'}`,
    safeString(panelKey) || 'default',
  ];

  return parts.join(':');
};

export const loadNodeTestState = (storageKey, fallbackValue) => {
  if (typeof window === 'undefined' || !window.localStorage || !storageKey) {
    return fallbackValue;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return fallbackValue;

    const parsed = JSON.parse(raw);
    if (parsed && Object.prototype.hasOwnProperty.call(parsed, 'data')) {
      return parsed.data;
    }

    return fallbackValue;
  } catch {
    return fallbackValue;
  }
};

export const saveNodeTestState = (storageKey, data) => {
  if (typeof window === 'undefined' || !window.localStorage || !storageKey) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      data,
    }));
  } catch {
    // Ignore localStorage quota or serialization issues.
  }
};

// Test delivery overrides (redirect email/SMS destinations) are personal to the
// tester, so they are stored per logged-in user rather than per workflow/node.
const USER_DELIVERY_OVERRIDE_PREFIX = 'workflow-test-delivery-override';

const buildUserDeliveryOverrideKey = () => {
  const userId = safeString(getUser()?.ID);
  return `${USER_DELIVERY_OVERRIDE_PREFIX}:${userId || 'unknown-user'}`;
};

export const loadUserTestDeliveryOverrides = () => {
  const data = loadNodeTestState(buildUserDeliveryOverrideKey(), {});
  return {
    testOverrideEmail: safeString(data?.testOverrideEmail),
    testOverrideMobile: safeString(data?.testOverrideMobile),
  };
};

export const saveUserTestDeliveryOverrides = ({ testOverrideEmail, testOverrideMobile } = {}) => {
  saveNodeTestState(buildUserDeliveryOverrideKey(), {
    testOverrideEmail: safeString(testOverrideEmail),
    testOverrideMobile: safeString(testOverrideMobile),
  });
};

const SHARED_CONTEXT_PREFIX = 'workflow-shared-test-context';

const buildSharedContextKey = (workflowDefinitionID) =>
  `${SHARED_CONTEXT_PREFIX}:${safeString(workflowDefinitionID) || 'unknown'}`;

export const saveSharedTestOutput = (workflowDefinitionID, nodeId, nodeLabel, outputVariables) => {
  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    const key = buildSharedContextKey(workflowDefinitionID);
    const raw = window.localStorage.getItem(key);
    const existing = raw ? JSON.parse(raw) : { version: 1, outputs: {} };
    existing.outputs = existing.outputs ?? {};
    existing.outputs[safeString(nodeId) || 'unknown'] = {
      nodeId: safeString(nodeId),
      nodeLabel: safeString(nodeLabel),
      savedAt: new Date().toISOString(),
      variables: outputVariables ?? {},
    };
    window.localStorage.setItem(key, JSON.stringify(existing));
  } catch {
    // Ignore storage errors.
  }
};

export const loadSharedTestOutputs = (workflowDefinitionID) => {
  if (typeof window === 'undefined' || !window.localStorage) return [];

  try {
    const key = buildSharedContextKey(workflowDefinitionID);
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    const outputs = parsed?.outputs ?? {};
    return Object.keys(outputs)
      .map((nodeId) => {
        const entry = outputs[nodeId];
        if (!entry || !entry.variables || typeof entry.variables !== 'object') return null;
        return {
          nodeId: safeString(entry.nodeId || nodeId),
          nodeLabel: safeString(entry.nodeLabel),
          savedAt: safeString(entry.savedAt),
          variables: entry.variables,
        };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
};
